/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useMutateRequest } from "@ch/api/useMutation";
import StatusIndicatorIcon from "@ch/assets/icons/pack/StatusIndicatorIcon";
import TabIcon from "@ch/assets/icons/pack/TabIcon";
import Button from "@ch/components/Buttons/Button";
import TextInput from "@ch/components/Inputs/TextInput";
import type {
  Payments,
  ProgramPlansWithRelations,
  UserSubscriptions,
  UserSubscriptionsWithRelations,
} from "@ch/generated";
import { VoucherControllerService } from "@ch/generated/services/VoucherControllerService";
import useDialogue from "@ch/helpers/hooks/useDialogue";
import { NotifyError, formatCurrency } from "@ch/helpers/utils/helpers";
import { useAppSelector } from "@ch/redux-store/hooks/useAppSelector";
import { updateAppUserState } from "@ch/redux-store/user/actions";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { PAYSTACK_API_KEY, PAYSTACK_DEFAULT_EMAIL } from "@ch/api";
import ComponentLoader from "@ch/components/Loaders/ComponentLoader";
import useFirebaseAnalytics from "@ch/helpers/utils/firebase/hooks/useFirebaseAnalytics";
import { FirebaseEventEnum } from "@ch/helpers/utils/firebase/types";
import { usePaystackPayment } from "react-paystack";
import { twMerge } from "tailwind-merge";
import PaymentSuccessfulModal from "../../components/PaymentSuccessfulModal";
import useInitializePayment from "../../hooks/useInitializePayment";
import useRecordSubscription from "../../hooks/useRecordSubscription";
import PreferredLanguageModal from "../../components/PreferredLanguageModal";

interface PlanBenefitProps {
  hidePlanDetails: () => void;
  benefits: (string | undefined)[] | undefined;
  activeSubscription: boolean;
  planInfo: ProgramPlansWithRelations;
  hasExpired?: boolean | undefined;
}

const PlanBenefitPage = ({
  hidePlanDetails,
  benefits,
  activeSubscription,
  planInfo,
  hasExpired,
}: PlanBenefitProps) => {
  const dispatch = useDispatch();

  const { dialogueId, handleOpenDialogue } = useDialogue();
  const {
    dialogueId: languageModalId,
    handleOpenDialogue: handleOpenLanguageModal,
  } = useDialogue();
  const { paymentData, initializePayment, initializing } =
    useInitializePayment();

  const user = useAppSelector((state) => state?.user?.currentUser);

  const price: number = planInfo?.basePrice ?? 0;
  const programId = planInfo?.programId;

  // Do not use ?? operator here because it accepts an empty string
  // and this causes paystack error
  const paystackEmail = user?.email || PAYSTACK_DEFAULT_EMAIL;

  const [discount, setDiscount] = useState(0);
  const [amount, setAmount] = useState(price - discount + 100);
  const [code, setCode] = useState<string>("");
  const [newSubscription, setSubscription] = useState<
    UserSubscriptionsWithRelations | UserSubscriptions
  >();

  const total = price - discount;
  const { loading, recordSubscription } = useRecordSubscription();

  const { LogEvent } = useFirebaseAnalytics();

  const {
    data: voucher,
    loading: isLoading,
    trigger: mutate,
  } = useMutateRequest({
    service: VoucherControllerService.voucherControllerFindByCode,
    tag: "",
    onSuccess: (data) => {
      if (data?.discountType === "fixed") {
        const discountValue = data?.amount ?? 0;

        setDiscount(discountValue);
        setAmount(price - discountValue + 100);
      }
      if (data?.discountType === "percentage") {
        const discountValue = data?.amount ? (data.amount / 100) * amount : 0;

        setDiscount(discountValue);
        setAmount(price - discountValue + 100);
      }
    },
  });

  const initializePaystackPayment = usePaystackPayment({
    amount: amount * 100,
    email: paystackEmail,
    reference: paymentData?.reference,
    publicKey: PAYSTACK_API_KEY,
    phone: user?.phone,
  });

  const callback = ({
    data,
  }: {
    data: UserSubscriptionsWithRelations;
    _ref?: string | undefined;
  }) => {
    const subscriptionDetails: UserSubscriptionsWithRelations = {
      ...data,
      programId,
      plan: planInfo,
    };
    setSubscription(subscriptionDetails);

    handleOpenDialogue();

    dispatch(
      updateAppUserState({
        currentUser: {
          ...user,
          subscriptions: [...(user?.subscriptions ?? []), subscriptionDetails],
        },
      }),
    );

    // Check if this is the user's first ever subscription
    const isFirstTimeSubscription = !user?.subscriptions?.length;

    // If the program exists in the user's subscriptions, it is a renewal
    const existingSubscription = user?.subscriptions?.find(
      (subscription) => subscription.programId === programId,
    );

    const baseEventParams = {
      programId: subscriptionDetails.programId ?? "",
      planId: subscriptionDetails.planId ?? "",
      planName: subscriptionDetails?.plan?.title ?? "",
      programName: subscriptionDetails?.program?.title ?? "",
    };

    const subscriptionEventParams = {
      ...baseEventParams,
      userSubscriptionId: subscriptionDetails?.id ?? "",
    };

    if (isFirstTimeSubscription) {
      LogEvent(FirebaseEventEnum.FIRST_TIME_SUBSCRIPTION, baseEventParams);
    }

    if (existingSubscription) {
      LogEvent(FirebaseEventEnum.RENEW_SUBSCRIPTION, subscriptionEventParams);
    } else {
      LogEvent(FirebaseEventEnum.NEW_SUBSCRIPTION, subscriptionEventParams);
    }
  };

  const generateRequestBody = ({
    reference,
    transactionId,
  }: {
    reference: unknown;
    transactionId: string;
  }) => ({
    paymentData: {
      id: paymentData?.id,
      userId: user?.id,
      email: user?.email,
      payer: "paystack",
      amount,
      reference,
      transactionId,
      voucherId: voucher?.id ?? "",
      planId: planInfo?.id,
      programId,
    } as Payments,
  });

  const onSuccess = (e: any, paymentId?: string) => {
    const payload = generateRequestBody({
      reference: e.reference,
      transactionId: e.trans,
    });
    const requestBody = {
      ...payload,
      paymentData: { ...payload.paymentData, id: paymentId },
    };

    void recordSubscription(requestBody, (data) => {
      callback({ data });
    });
  };

  const handlePayWithPaystack = () =>
    initializePayment(
      {
        userId: user?.id,
        email: user?.email,
        payer: "paystack",
        amount,
        voucherId: voucher?.id ?? "",
        planId: planInfo?.id,
        programId,
      },
      (data) => {
        initializePaystackPayment({
          config: {
            amount: amount * 100,
            email: paystackEmail,
            reference: data?.reference,
          },
          onSuccess: (e) => onSuccess(e, data?.id),
          onClose: () => NotifyError("Transaction incomplete"),
        });
      },
    );

  const handleCheckout = () => {
    if (!user?.preferredLanguageIds?.length) {
      handleOpenLanguageModal();
      return;
    }

    if (total === 0 || total < 1) {
      void initializePayment(
        {
          userId: user?.id,
          email: user?.email,
          payer: "paystack",
          amount: total,
          voucherId: voucher?.id ?? "",
          planId: planInfo?.id,
          programId,
        },
        (newPaymentData) => {
          void recordSubscription(
            {
              paymentData: {
                id: newPaymentData?.id,
                userId: user?.id,
                email: user?.email,
                payer: "paystack",
                amount: total,
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                reference: newPaymentData?.reference!,
                transactionId: newPaymentData?.transactionId,
                voucherId: voucher?.id ?? "",
                planId: planInfo?.id,
                programId,
              },
            },
            (data) =>
              callback({
                data,
                _ref: newPaymentData?.reference,
              }),
          );
        },
      );
    } else void handlePayWithPaystack();
  };

  const handleApplyVoucher = () =>
    mutate({
      code,
      planId: planInfo?.id,
      programId,
    });

  return (
    <div className="pb-6 md:pb-8">
      {loading ? (
        <ComponentLoader />
      ) : (
        <>
          <div className="flex gap-4 rounded-2xl border border-orange bg-orange-300 p-4 md:p-6">
            <TabIcon fill="#FD6E5E" />
            <div className="font-semibold">
              <p className="">{planInfo?.title}</p>
              <p className="text-2xl/7">
                {planInfo.basePrice === 0 ? (
                  "Free"
                ) : (
                  <span>
                    {`₦${formatCurrency(planInfo?.basePrice)}`}.
                    <span className="text-sm text-gray">00</span>
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="my-6 rounded-2xl bg-white p-4 md:p-6">
            <p className="border-b pb-4 text-lg font-semibold">Benefits</p>
            <div className="grid-col-1 grid gap-4 pt-4 font-normal text-[#16133D] md:grid-cols-2">
              {benefits?.map((data) => (
                <div className="flex items-center gap-2" key={data}>
                  <StatusIndicatorIcon
                    fill="#FD6E5E"
                    stroke="#FFE9E5"
                    width={8}
                  />
                  <p className="">{data}</p>
                </div>
              ))}
            </div>
          </div>
          {discount === 0 && (!activeSubscription || hasExpired) ? (
            <div className="my-6 flex gap-4">
              <TextInput
                className="w-52"
                maxLength={200}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter discount code"
              />
              <Button
                className={twMerge(
                  "bg-orange",
                  !code && "opacity-70 hover:opacity-70",
                )}
                disabled={!code}
                isLoading={isLoading}
                onClick={handleApplyVoucher}
              >
                Apply
              </Button>
            </div>
          ) : null}

          {!!discount && (
            <div className="my-6 flex w-fit flex-col rounded-2xl border border-gray-400 bg-white p-4 md:p-6">
              <div className="gap-4">
                <p className="text-sm">
                  Voucher:{" "}
                  <span className="font-semibold">{voucher?.code}</span>
                </p>
              </div>
              <div className="flex gap-6 pt-2 lg:flex-row lg:justify-between">
                <div className="border-b border-none border-b-gray-400">
                  <h4>₦{formatCurrency(price)}</h4>
                  <p className="text-sm">Amount</p>
                </div>
                <div className="px-3">
                  <h4>₦{formatCurrency(discount)}</h4>
                  <p className="text-sm">Discount</p>
                </div>
                <div className="px-3 text-blue">
                  <h3 className="">₦{formatCurrency(total)}</h3>
                  <p className="text-sm">New Total</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <div className="">
              <Button
                className="-ml-4"
                intent="ghost"
                leftIcon="chevron-left-filled"
                leftIconProps={{
                  size: 30,
                }}
                onClick={() => hidePlanDetails()}
              >
                Back
              </Button>
            </div>

            {!activeSubscription || hasExpired ? (
              <Button isLoading={initializing} onClick={handleCheckout}>
                Checkout
              </Button>
            ) : null}
          </div>
        </>
      )}
      <PaymentSuccessfulModal
        modalId={dialogueId}
        price={price}
        reference={paymentData?.reference ?? ""}
        subscription={newSubscription ?? {}}
      />
      <PreferredLanguageModal
        modalId={languageModalId}
        onClose={() => {}}
        onSuccess={handleCheckout}
      />
    </div>
  );
};

export default PlanBenefitPage;
